<template>
  <slot :mutated-entity="mutatedEntity" />
</template>

<script setup>
import {
  ref,
  reactive,
  computed,
  provide,
  onMounted,
  onBeforeUnmount,
  useRouter,
  useAsyncData
} from "#imports";
import "#blokkli-build/styles.css";
import getAdapter from "#blokkli-build/edit-adapter";
import {
  INJECT_EDIT_CONTEXT,
  INJECT_IS_PREVIEW,
  INJECT_MUTATED_FIELDS_MAP
} from "#blokkli/helpers/symbols";
import { frameEventBus } from "#blokkli/helpers/frameEventBus";
import broadcastProvider from "#blokkli/helpers/broadcastProvider";
import { getFieldKey, intersects } from "#blokkli/helpers";
import { eventBus } from "#blokkli/helpers/eventBus";
import definitionProvider from "../../helpers/definitionProvider";
const props = defineProps({
  entity: { type: null, required: false },
  entityType: { type: String, required: true },
  entityUuid: { type: String, required: true },
  entityBundle: { type: String, required: true },
  language: { type: String, required: true }
});
defineSlots();
const context = computed(() => {
  return {
    entityType: props.entityType,
    entityBundle: props.entityBundle,
    entityUuid: props.entityUuid,
    language: props.language
  };
});
const adapter = await getAdapter(context);
const router = useRouter();
const broadcast = broadcastProvider();
const definitions = definitionProvider();
let timeout = null;
let lastChanged = 0;
const mutatedFieldsMap = reactive({});
const mutatedOptions = reactive({});
const mutatedEntityFromState = ref(null);
const mutatedEntity = computed(
  () => mutatedEntityFromState.value || props.entity
);
const { data, refresh, error } = await useAsyncData(
  () => adapter.loadState().then((v) => adapter.mapState(v))
);
function updateMutatedFields(fields) {
  fields.forEach((field) => {
    const key = getFieldKey(field.entityUuid, field.name);
    mutatedFieldsMap[key] = field;
  });
}
const updateState = () => {
  const fields = data.value?.mutatedState?.fields || [];
  updateMutatedFields(fields);
  mutatedEntityFromState.value = data.value?.mutatedEntity;
  const options = mutatedOptions.value || data.value?.mutatedState?.mutatedOptions || {};
  const optionKeys = Object.keys(options);
  for (let i = 0; i < optionKeys.length; i++) {
    const key = optionKeys[i];
    if (key) {
      const newOptions = options[key];
      const existing = mutatedOptions[key];
      if (!existing || JSON.stringify(existing) !== JSON.stringify(newOptions)) {
        mutatedOptions[key] = newOptions;
      }
    }
  }
};
updateState();
const isPreview = computed(() => !error.value);
provide(INJECT_MUTATED_FIELDS_MAP, mutatedFieldsMap);
provide(INJECT_IS_PREVIEW, isPreview);
provide(INJECT_EDIT_CONTEXT, {
  mutatedOptions,
  eventBus,
  definitions
});
function onMessage(e) {
  if (e.data && typeof e.data === "object" && e.data.name && typeof e.data.name === "string" && e.data.name.startsWith("blokkli__")) {
    const name = e.data.name.replace("blokkli__", "");
    const data2 = e.data.data;
    frameEventBus.emit(name, data2);
  }
}
const onMouseDown = () => {
  broadcast.emit("previewFocused");
};
function checkChangedDate() {
  if (!isPreview.value) {
    return;
  }
  clearTimeout(timeout);
  const delay = adapter.getLastChanged ? 1e3 : 5e3;
  timeout = setTimeout(async () => {
    const changed = adapter.getLastChanged ? await adapter.getLastChanged() : Date.now();
    if (changed) {
      if (lastChanged !== 0 && lastChanged !== changed) {
        await refresh();
        updateState();
      }
      lastChanged = changed;
      checkChangedDate();
    }
  }, delay);
}
const onWheel = (e) => {
  if (e.ctrlKey) {
    e.preventDefault();
  }
};
const isInIframe = () => window.parent !== window;
const onFocusItem = (uuid) => {
  const el = document.querySelector(`[data-uuid="${uuid}"]`);
  if (el) {
    const elRect = el.getBoundingClientRect();
    if (!intersects(elRect, {
      x: 0,
      y: 0,
      width: window.innerWidth,
      height: window.innerHeight
    })) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center"
      });
    }
  }
};
const onUpdateOption = (option) => {
  const { uuid, key, value } = option;
  if (!mutatedOptions[uuid]) {
    mutatedOptions[uuid] = {};
  }
  mutatedOptions[uuid][key] = value;
};
onMounted(() => {
  if (isInIframe()) {
    frameEventBus.on("mutatedFields", updateMutatedFields);
    frameEventBus.on("focus", onFocusItem);
    frameEventBus.on("updateOption", onUpdateOption);
    document.body.classList.add("bk-body-preview");
    document.documentElement.classList.add("bk-html-preview");
    window.addEventListener("message", onMessage);
    window.addEventListener("wheel", onWheel, { passive: false });
    document.documentElement.addEventListener("mousedown", onMouseDown);
    router.push = () => Promise.resolve();
    router.replace = () => Promise.resolve();
  } else {
    checkChangedDate();
  }
});
onBeforeUnmount(() => {
  clearTimeout(timeout);
  document.body.classList.remove("bk-body-preview");
  document.documentElement.classList.remove("bk-html-preview");
  window.removeEventListener("wheel", onWheel);
  window.removeEventListener("message", onMessage);
  document.documentElement.removeEventListener("mousedown", onMouseDown);
  frameEventBus.off("mutatedFields", updateMutatedFields);
  frameEventBus.off("focus", onFocusItem);
  frameEventBus.off("updateOption", onUpdateOption);
});
</script>
